import adminAxiosInstance from "./api/adminAxiosInstance";

/**
 * Busca os registros de auditoria com filtros e paginação.
 * @param {object} params - Parâmetros de busca.
 * @param {number} params.limit - Número de itens por página.
 * @param {number} params.offset - Número de itens a pular.
 * @param {string} [params.searchTerm] - Termo para buscar em nome do admin ou ação.
 * @param {string} [params.startDate] - Data de início no formato YYYY-MM-DD.
 * @param {string} [params.endDate] - Data final no formato YYYY-MM-DD.
 * @returns {Promise<{totalCount: number, audits: Array<object>}>}
 */
const searchAudits = async (params) => {
  // URLSearchParams constrói a query string de forma segura e limpa,
  // ignorando parâmetros nulos ou vazios.
  const queryParams = new URLSearchParams();

  if (params.limit) queryParams.append("limit", params.limit);
  if (params.offset) queryParams.append("offset", params.offset);
  if (params.searchTerm) queryParams.append("searchTerm", params.searchTerm);
  if (params.startDate) queryParams.append("startDate", params.startDate);
  if (params.endDate) queryParams.append("endDate", params.endDate);

  const response = await adminAxiosInstance.get(
    `audit/search?${queryParams.toString()}`
  );
  return response.data;
};

const searchClientAudits = async (params) => {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.offset) queryParams.append("offset", params.offset);
  if (params.searchTerm) queryParams.append("searchTerm", params.searchTerm);
  if (params.startDate) queryParams.append("startDate", params.startDate);
  if (params.endDate) queryParams.append("endDate", params.endDate);

  // Nota: A rota aqui é clientAudit/search, conforme seu controller backend
  const response = await adminAxiosInstance.get(
    `clientAudit/search?${queryParams.toString()}`
  );
  return response.data;
};

/**
 * Busca os administradores com sessão ativa no momento.
 * @returns {Promise<{count: number, activeAdmins: Array<object>}>}
 */
const getActiveUsers = async () => {
  const response = await adminAxiosInstance.get("audit/active-users");
  return response.data;
};

/**
 * Registra no log de auditoria que um admin acessou a página de um cliente.
 * @param {number} clientId - O ID do cliente que foi acessado.
 * @param {string} clientName - O nome do cliente que foi acessado.
 * @returns {Promise<void>}
 */
const registerAdminClientAccess = async (clientId, clientName) => {
  try {
    // A rota corresponde exatamente à do seu controller: api/audit/register-admin-client-access
    await adminAxiosInstance.post("audit/register-admin-client-access", {
      clientId,
      clientName,
    });
  } catch (error) {
    // É importante não quebrar a interface do usuário se o log falhar.
    // Apenas registramos o erro no console para depuração.
    console.error("Falha ao registrar o acesso à página do cliente:", error);
  }
};

/**
 * Registra no log de auditoria que um admin visualizou o telefone de um cliente.
 * @param {number} clientId - O ID do cliente.
 * @param {string} clientName - O nome do cliente.
 * @returns {Promise<void>}
 */
const registerPhoneView = async (clientId, clientName) => {
  try {
    await adminAxiosInstance.post("audit/register-phone-view", {
      clientId,
      clientName,
    });
  } catch (error) {
    console.error("Falha ao registrar visualização do telefone:", error);
  }
};

/**
 * Registra no log de auditoria que um admin extraiu/exportou dados.
 * @param {string} fileName - O nome ou descrição do arquivo/dado que foi extraído.
 * @returns {Promise<void>}
 */
const registerDataExtraction = async (fileName) => {
  try {
    await adminAxiosInstance.post("audit/register-data-extraction", {
      fileName,
    });
  } catch (error) {
    console.error("Falha ao registrar a extração de dados:", error);
  }
};

/**
 * Registra que um admin acessou uma aba/página principal.
 * @param {string} tabName - O nome da aba (ex: "Clientes", "Contratos").
 */
const registerTabAccess = async (tabName) => {
  try {
    await adminAxiosInstance.post("audit/register-tab-access", { tabName });
  } catch (error) {
    console.error(`Falha ao registrar acesso à aba ${tabName}:`, error);
  }
};

/**
 * Registra que um admin acessou os detalhes de um contrato.
 * @param {number} contractId - O ID do contrato.
 * @param {string} clientName - O nome do cliente do contrato.
 */
const registerContractAccess = async (contractId, clientName) => {
  try {
    await adminAxiosInstance.post("audit/register-contract-access", {
      contractId,
      clientName,
    });
  } catch (error) {
    console.error(
      `Falha ao registrar acesso ao contrato ${contractId}:`,
      error
    );
  }
};

/**
 * Registra que um admin aprovou ou negou um contrato.
 * @param {number} contractId - O ID do contrato.
 * @param {string} clientName - O nome do cliente do contrato.
 * @param {boolean} wasApproved - `true` se foi aprovado, `false` se foi negado.
 */
const registerContractApproval = async (
  contractId,
  clientName,
  wasApproved
) => {
  try {
    await adminAxiosInstance.post("audit/register-contract-approval", {
      contractId,
      clientName,
      wasApproved,
    });
  } catch (error) {
    console.error(
      `Falha ao registrar aprovação do contrato ${contractId}:`,
      error
    );
  }
};

/**
 * Registra que um admin aprovou ou negou um saque.
 * @param {number} withdrawId - O ID do saque.
 * @param {string} clientName - O nome do cliente do saque.
 * @param {boolean} wasApproved - `true` se foi aprovado, `false` se foi negado.
 */
const registerWithdrawalApproval = async (
  withdrawId,
  clientName,
  wasApproved
) => {
  try {
    await adminAxiosInstance.post("audit/register-withdrawal-approval", {
      withdrawId,
      clientName,
      wasApproved,
    });
  } catch (error) {
    console.error(
      `Falha ao registrar aprovação do saque ${withdrawId}:`,
      error
    );
  }
};

/**
 * Registra que um admin usou a função de personificação para logar na conta de um cliente.
 * @param {number} clientId - O ID do cliente.
 * @param {string} clientName - O nome do cliente.
 */
const registerAdminClientLogin = async (clientId, clientName) => {
  try {
    await adminAxiosInstance.post("audit/register-client-login", {
      clientId,
      clientName,
    });
  } catch (error) {
    console.error("Falha ao registrar o login na conta do cliente:", error);
  }
};

export const auditService = {
  searchAudits,
  getActiveUsers,
  registerAdminClientAccess,
  registerPhoneView,
  registerDataExtraction,
  registerTabAccess,
  registerContractAccess,
  registerContractApproval,
  registerWithdrawalApproval,
  registerAdminClientLogin,
  searchClientAudits
};
